/**
 * Storefront app-wide UX flags. Sourced from `import.meta.env` at build
 * time (Vite inlines `VITE_*` vars into the bundle), with safe defaults
 * for local dev.
 *
 * Add a new flag here whenever a feature needs an admin-toggleable
 * Y / N switch — keeping them in one file means the per-feature code
 * stays clean and the deployment team has a single surface to flip.
 */

/**
 * Should the sign-in / sign-up modals expose a "Company name" textbox?
 *
 *   `Y` → field rendered, user can type / override the tenant code.
 *   `N` → field hidden, tenant code derived from `window.location.hostname`
 *         (subdomain or the second-level domain on a bare host).
 *
 * Default is `N` — most production tenants live on their own subdomain
 * (`sso.kristysoftware.com`, `acme.kristymart.in`, …) so the user
 * never needs to know the tenant code. White-label / multi-tenant
 * resellers can flip it to `Y` via `.env`:
 *
 *   VITE_SHOW_COMPANY_NAME=Y   (any case; "yes" / "true" / "1" also work)
 *
 * Bug ref: AI Agent 2026-05-26 — "company name should be sub domain or
 * domain name and hide by default … if Y then show company name with
 * textbox otherwise hide".
 */
export function shouldShowCompanyNameInput(): boolean {
  /* Resolution priority (first match wins):
       1. window.__KSS_CONFIG__.showCompanyNameInput  (runtime config —
          editable post-deploy via `public/runtime-config.js`)
       2. VITE_SHOW_COMPANY_NAME                       (build-time env)
       3. 'N'                                          (default) */
  let raw = 'N';
  try {
    if (typeof window !== 'undefined') {
      const rt = (window as unknown as { __KSS_CONFIG__?: { showCompanyNameInput?: string } })
        .__KSS_CONFIG__;
      if (rt?.showCompanyNameInput != null) {
        raw = String(rt.showCompanyNameInput);
      }
    }
    if (raw === 'N') {
      const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
      raw = String(env?.VITE_SHOW_COMPANY_NAME ?? 'N');
    }
  } catch {
    /* fall through with default */
  }
  const v = raw.trim().toLowerCase();
  return v === 'y' || v === 'yes' || v === 'true' || v === '1';
}

/**
 * Derive a tenant code from the browser's current hostname.
 *
 *   `sso.kristysoftware.com`         → `sso`
 *   `acme.kristymart.in`             → `acme`
 *   `panel.acme.kristymart.in`       → `acme`     (skip well-known app prefixes)
 *   `www.kontic.com`                 → `kontic`   (skip → next label is SLD)
 *   `panel.kontic.com`               → `kontic`
 *   `kontic.com`                     → `kontic`   (bare domain — SLD is tenant)
 *   `kontic.co.in`                   → `kontic`   (bare ccTLD)
 *   `kristysoftware.com`             → `kristysoftware`
 *   `localhost` / `127.0.0.1` / `*.local` → `sso` (dev fallback)
 *
 * Returns an empty string only when running outside the browser (SSR);
 * callers should treat empty as "let the backend's Host-header
 * resolver decide".
 *
 * Bug ref: AI Agent 2026-06-11 — "if sub domain not found then domain
 * is tenant code". Also fixed the previous fallback that returned a
 * generic label like `www` or `panel` as the tenant for hosts like
 * `www.kontic.com`.
 */
export function getTenantFromHost(): string {
  if (typeof window === 'undefined') return '';
  const host = (window.location.hostname || '').toLowerCase().trim();
  if (!host) return '';

  // Local dev — always sso so the live API's seeded tenant works.
  // Also covers LAN IPs (192.168.x.x, 10.x.x.x, etc.) so a `npm run
  // dev -- --host` session served over the LAN doesn't try to treat
  // `192` as a tenant code.
  if (
    host === 'localhost' ||
    host.endsWith('.local') ||
    /^\d{1,3}(?:\.\d{1,3}){3}$/.test(host)
  ) {
    return 'sso';
  }

  const parts = host.split('.').filter(Boolean);
  if (parts.length === 0) return '';

  // Bare host (single label or 2 labels — e.g. kontic.com / kontic.in):
  // the first label IS the tenant.
  if (parts.length <= 2) return parts[0]!;

  /* Multi-label subdomain. Skip generic app prefixes ("www", "panel",
     "admin", "api", "app") and return the FIRST non-generic label.
     For `www.kontic.com` the loop walks `['www']`, none are
     non-generic, so we fall through to `parts[parts.length - 2]` —
     the SLD (`kontic`). For `panel.acme.kristymart.in` the loop hits
     `acme` and returns it. */
  const generic = new Set(['www', 'panel', 'admin', 'api', 'app', 'm', 'mobile']);
  for (const label of parts.slice(0, parts.length - 2)) {
    if (!generic.has(label)) return label;
  }
  /* All leading labels were generic — the SLD (parts[length-2]) is
     the tenant. Previously this fell back to `parts[length-3]` which
     for 3-label hosts (`www.kontic.com`) gave back the generic
     `'www'`. */
  return parts[parts.length - 2]!;
}
